import { useState, useEffect, useRef } from "react";

/**
 * Custom hook: Exam countdown timer.
 * Uses ref pattern to avoid stale closures without re-triggering the interval.
 *
 * @param {boolean} isActive - True when the exam is running
 * @param {number} durationSeconds - Total exam duration in seconds
 * @param {Function} onTimeout - Callback fired when timer reaches 0
 */
export function useExamTimer(isActive, durationSeconds, onTimeout) {
  const [timeLeft, setTimeLeft] = useState(durationSeconds);
  const [showFiveMinAlert, setShowFiveMinAlert] = useState(false);
  // Use ref to avoid stale closure in setInterval callback
  const fiveMinShownRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);

  // Keep callback ref fresh on every render (no dep array — intentional pattern)
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  });

  // Reset timer state when exam starts
  useEffect(() => {
    if (isActive) {
      setTimeLeft(durationSeconds);
      fiveMinShownRef.current = false;
      setShowFiveMinAlert(false);
    }
  }, [isActive, durationSeconds]);

  // Countdown interval — only depends on isActive
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        if (prev === 300 && !fiveMinShownRef.current) {
          fiveMinShownRef.current = true;
          setShowFiveMinAlert(true);
          setTimeout(() => setShowFiveMinAlert(false), 15000);
        }
        if (prev <= 1) {
          clearInterval(interval);
          onTimeoutRef.current?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive]); // Stable: onTimeoutRef is a ref, no dep needed

  return {
    timeLeft,
    showFiveMinAlert,
    dismissFiveMinAlert: () => setShowFiveMinAlert(false),
  };
}
